import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { COMPANY_PROFILE } from '../config/companyProfile';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  RefreshCw, 
  Search,
  Eye,
  Truck,
  RotateCcw,
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  Filter,
  Printer
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DCRegisterPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  // Data state
  const [dcs, setDcs] = useState([]);
  const [modes, setModes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [selectedMode, setSelectedMode] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // View dialog
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedDC, setSelectedDC] = useState(null);
  
  // Print ref
  const printRef = useRef();
  
  // Print handler
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: selectedDC ? `DC_${selectedDC.dc_number}` : 'DeliveryChallan'
  });

  // Fetch DC modes
  const fetchModes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/delivery-challans/modes`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setModes(data);
      }
    } catch (err) {
      console.error('Failed to fetch modes:', err);
    }
  }, [getAuthHeader]);

  // Fetch DCs with filters
  const fetchDCs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/delivery-challans?limit=200`;
      if (selectedMode !== 'all') url += `&mode=${selectedMode}`;
      if (selectedStatus !== 'all') url += `&status=${selectedStatus}`;
      if (dateFrom) url += `&from_date=${dateFrom}`;
      if (dateTo) url += `&to_date=${dateTo}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await fetch(url, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setDcs(data.items || data);
      }
    } catch (err) {
      console.error('Failed to fetch DCs:', err);
      toast.error('Failed to load delivery challans');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, selectedMode, selectedStatus, dateFrom, dateTo, searchQuery]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/delivery-challans/register`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data.summary);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchModes();
    fetchSummary();
  }, [fetchModes, fetchSummary]);

  useEffect(() => {
    fetchDCs();
  }, [fetchDCs]);

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Draft', variant: 'secondary', icon: Clock },
      pending_dispatch: { label: 'Pending Dispatch', variant: 'warning', icon: Truck },
      dispatched: { label: 'Dispatched', variant: 'success', icon: CheckCircle2 },
      returned: { label: 'Returned', variant: 'default', icon: RotateCcw },
      cancelled: { label: 'Cancelled', variant: 'destructive', icon: XCircle }
    };
    const config = statusConfig[status] || { label: status, variant: 'secondary', icon: Package };
    const Icon = config.icon;
    
    return (
      <Badge 
        variant={config.variant}
        className="flex items-center gap-1"
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Get mode badge
  const getModeBadge = (mode, modeLabel) => {
    const modeColors = {
      ojc: 'bg-blue-100 text-blue-800',
      purchase_return: 'bg-orange-100 text-orange-800',
      sample_outward: 'bg-purple-100 text-purple-800',
      inter_unit_transfer: 'bg-green-100 text-green-800',
      scrap_disposal: 'bg-gray-100 text-gray-800',
      loan_rental: 'bg-pink-100 text-pink-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${modeColors[mode] || 'bg-gray-100 text-gray-800'}`}>
        {modeLabel || mode}
      </span>
    );
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // View DC details
  const handleViewDC = (dc) => {
    setSelectedDC(dc);
    setViewDialog(true);
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="dc-register-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Delivery Challan Register</h1>
          <p className="text-sm text-gray-500">Universal document for non-sales outward movements</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { fetchDCs(); fetchSummary(); }}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => navigate('/store/dc/create')}
            data-testid="create-dc-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create DC
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{summary.total || 0}</div>
              <p className="text-xs text-gray-500">Total DCs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">
                {summary.by_status?.pending_dispatch?.count || 0}
              </div>
              <p className="text-xs text-gray-500">Pending Dispatch</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">
                {summary.by_status?.dispatched?.count || 0}
              </div>
              <p className="text-xs text-gray-500">Dispatched</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">
                {summary.by_mode?.ojc?.count || 0}
              </div>
              <p className="text-xs text-gray-500">OJC DCs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">
                {summary.by_mode?.purchase_return?.count || 0}
              </div>
              <p className="text-xs text-gray-500">Purchase Returns</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-600">
                {summary.by_status?.draft?.count || 0}
              </div>
              <p className="text-xs text-gray-500">Drafts</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="DC number, party name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <div className="w-[180px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Mode</label>
              <Select value={selectedMode} onValueChange={setSelectedMode}>
                <SelectTrigger data-testid="mode-filter">
                  <SelectValue placeholder="All Modes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modes</SelectItem>
                  {modes.map(mode => (
                    <SelectItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_dispatch">Pending Dispatch</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                  <SelectItem value="returned">Returned</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[150px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">From Date</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from"
              />
            </div>
            
            <div className="w-[150px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">To Date</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to"
              />
            </div>
            
            <Button
              variant="outline"
              onClick={() => {
                setSelectedMode('all');
                setSelectedStatus('all');
                setSearchQuery('');
                setDateFrom('');
                setDateTo('');
              }}
              data-testid="clear-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* DC Table */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading...</span>
            </div>
          ) : dcs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No delivery challans found</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate('/store/dc/create')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First DC
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>DC Number</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Party</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Gate Pass</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dcs.map((dc) => (
                  <TableRow key={dc.id} data-testid={`dc-row-${dc.dc_number}`}>
                    <TableCell className="font-medium">{dc.dc_number}</TableCell>
                    <TableCell>{getModeBadge(dc.mode, dc.mode_label)}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{dc.party_name}</div>
                        <div className="text-xs text-gray-500 capitalize">{dc.party_type}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {dc.items?.length || 0} item(s)
                        <div className="text-xs text-gray-500">
                          Qty: {dc.total_quantity?.toLocaleString() || 0}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {dc.gate_pass_number ? (
                        <span className="text-blue-600 font-medium">{dc.gate_pass_number}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(dc.status)}</TableCell>
                    <TableCell className="text-sm">{formatDate(dc.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDC(dc)}
                        data-testid={`view-dc-${dc.dc_number}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View DC Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {selectedDC?.dc_number}
              </span>
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-dc-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          {selectedDC && (
            <div ref={printRef} className="space-y-4 p-2">
              {/* Print Header — mirrors POPrint.js company block (same source of truth) */}
              <div className="hidden print:block border-b pb-4 mb-4">
                <div className="text-center">
                  <h1 className="text-2xl font-bold tracking-wide">{COMPANY_PROFILE.name}</h1>
                  <p className="text-xs text-gray-700 mt-1">{COMPANY_PROFILE.address_line_1}</p>
                  <p className="text-xs text-gray-700">{COMPANY_PROFILE.address_line_2}</p>
                  <p className="text-xs text-gray-700">GSTIN: <span className="font-mono">{COMPANY_PROFILE.gstin}</span></p>
                </div>
                <div className="text-center mt-3 pt-2 border-t">
                  <h2 className="text-xl font-bold">DELIVERY CHALLAN</h2>
                  <p className="text-sm font-mono">{selectedDC.dc_number}</p>
                  <p className="text-xs text-gray-600">{selectedDC.mode_label || selectedDC.mode}</p>
                </div>
              </div>
              
              {/* DC Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Mode</label>
                  <div>{getModeBadge(selectedDC.mode, selectedDC.mode_label)}</div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Status</label>
                  <div>{getStatusBadge(selectedDC.status)}</div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Party</label>
                  <div className="font-medium">{selectedDC.party_name}</div>
                  <div className="text-xs text-gray-500 capitalize">{selectedDC.party_type}</div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Gate Pass</label>
                  <div className="font-medium text-blue-600">
                    {selectedDC.gate_pass_number || '-'}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Created</label>
                  <div>{formatDate(selectedDC.created_at)}</div>
                  <div className="text-xs text-gray-500">{selectedDC.created_by_name}</div>
                </div>
                {selectedDC.returnable && (
                  <div>
                    <label className="text-xs text-gray-500">Expected Return</label>
                    <div>{formatDate(selectedDC.expected_return_date) || '-'}</div>
                  </div>
                )}
              </div>
              
              {/* Transport */}
              {(selectedDC.vehicle_number || selectedDC.driver_name) && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium mb-2">Transport Details</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <label className="text-xs text-gray-500">Vehicle</label>
                      <div>{selectedDC.vehicle_number || '-'}</div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Driver</label>
                      <div>{selectedDC.driver_name || '-'}</div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Contact</label>
                      <div>{selectedDC.driver_contact || '-'}</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Items */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-2">Items ({selectedDC.items?.length || 0})</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedDC.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell>{item.unit}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {item.rejection_reason || item.remarks || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Remarks */}
              {selectedDC.remarks && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium mb-1">Remarks</h4>
                  <p className="text-sm text-gray-600">{selectedDC.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
};

export default DCRegisterPage;
